<template>
  <Teleport to="body">
    <div v-if="open" class="notification-modal-overlay">
      <div class="notification-modal">
        <div class="notification-modal__header">
          <span class="notification-modal__icon material-symbols-outlined" :class="`notification-modal__icon--${notification?.type}`">
            {{ getIcon(notification?.type) }}
          </span>
          <h3 class="notification-modal__title">{{ notification?.title }}</h3>
          <button type="button" class="notification-modal__close" @click="close">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="notification-modal__body">
          <div class="notification-modal__message" v-html="formattedMessage"></div>
        </div>
        <div class="notification-modal__footer">
          <span class="notification-modal__time">{{ formatDateTime(notification?.createdAt) }}</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { getLanguage } from '../services/i18nService'

const props = defineProps({
  open: { type: Boolean, default: false },
  notification: { type: Object, default: null },
})

const emit = defineEmits(['close'])

const close = () => {
  emit('close')
}

const getIcon = (type) => {
  const icons = {
    // Біллінгові типи
    invoice_issued: 'receipt_long',
    payment_reminder: 'schedule',
    payment_overdue: 'warning',
    warning: 'warning',
    suspension_notice: 'pause_circle',
    block_notice: 'block',
    unblock_notice: 'check_circle',
    custom: 'mail',
    // Системні типи
    geozone_enter: 'login',
    geozone_exit: 'logout',
    tracker_loss: 'signal_wifi_off',
    tracker_online: 'signal_wifi_4_bar',
    sensor_trigger: 'sensors',
    alarm_trigger: 'crisis_alert',
    dtc: 'car_repair',
    tacho: 'timer',
    sent_etoll_no_fix: 'gps_off',
    station_card: 'local_gas_station',
    station_intake: 'water_drop',
  }
  return icons[type] || 'notifications'
}

const formattedMessage = computed(() => {
  if (!props.notification?.message) return ''
  // Convert line breaks to <br> if not already HTML
  let message = props.notification.message
  // If message doesn't contain HTML tags, convert newlines
  if (!/<[a-z][\s\S]*>/i.test(message)) {
    message = message.replace(/\n/g, '<br>')
  }
  return message
})

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  let date
  if (dateString.endsWith('Z') || dateString.includes('+')) {
    date = new Date(dateString)
  } else {
    date = new Date(dateString.replace(' ', 'T') + 'Z')
  }

  const lang = getLanguage()
  const locale = lang === 'uk' ? 'uk-UA' : lang === 'ru' ? 'ru-RU' : 'en-US'

  return date.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
</script>

<style scoped>
.notification-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10100;
  padding: 1rem;
}

.notification-modal {
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.notification-modal__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-default);
}

.notification-modal__icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  font-size: 22px;
  flex-shrink: 0;
}

.notification-modal__icon--warning,
.notification-modal__icon--payment_overdue {
  background: var(--amber-50);
  color: var(--warning-text);
}

.notification-modal__icon--block_notice {
  background: var(--danger-light);
  color: var(--danger-hover);
}

.notification-modal__icon--suspension_notice {
  background: var(--warning-light);
  color: var(--amber-600);
}

.notification-modal__icon--unblock_notice {
  background: var(--success-light);
  color: var(--success-hover);
}

.notification-modal__icon--invoice_issued,
.notification-modal__icon--payment_reminder {
  background: var(--border-default);
  color: var(--gray-500);
}

.notification-modal__icon--custom {
  background: var(--bg-tertiary);
  color: var(--text-muted);
}

/* Системні типи */
.notification-modal__icon--geozone_enter,
.notification-modal__icon--geozone_exit {
  background: var(--border-default);
  color: var(--primary-hover);
}

.notification-modal__icon--tracker_loss {
  background: var(--danger-light);
  color: var(--danger-hover);
}

.notification-modal__icon--tracker_online {
  background: var(--success-light);
  color: var(--success-hover);
}

.notification-modal__icon--sensor_trigger {
  background: var(--amber-50);
  color: var(--warning-text);
}

.notification-modal__icon--alarm_trigger {
  background: var(--danger-light);
  color: var(--danger-hover);
}

.notification-modal__title {
  flex: 1;
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--text-heading);
  margin: 0;
}

.notification-modal__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: var(--bg-tertiary);
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--text-muted);
  transition: all 0.15s;
  flex-shrink: 0;
}

.notification-modal__close:hover {
  background: var(--border-default);
  color: var(--text-heading);
}

.notification-modal__body {
  flex: 1;
  padding: 1.25rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.notification-modal__message {
  font-size: var(--text-base);
  line-height: 1.6;
  color: var(--gray-700);
}

.notification-modal__message :deep(p) {
  margin: 0 0 0.75rem;
}

.notification-modal__message :deep(p:last-child) {
  margin-bottom: 0;
}

.notification-modal__message :deep(strong),
.notification-modal__message :deep(b) {
  font-weight: 600;
  color: var(--text-heading);
}

.notification-modal__message :deep(a) {
  color: var(--primary);
  text-decoration: underline;
}

.notification-modal__message :deep(ul),
.notification-modal__message :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.notification-modal__message :deep(li) {
  margin-bottom: 0.25rem;
}

.notification-modal__footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid var(--border-default);
  background: var(--bg-secondary);
}

.notification-modal__time {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

@media (max-width: 480px) {
  .notification-modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .notification-modal {
    max-width: none;
    max-height: 100vh;
    max-height: 100dvh;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
  }

  .notification-modal__body {
    flex: 1;
    min-height: 0;
  }

  /* Учитываем "челку" iPhone (safe area) */
  .notification-modal__header {
    padding-top: calc(0.75rem + env(safe-area-inset-top, 0px));
  }

  .notification-modal__footer {
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  }
}
</style>
